// ─────────────────────────────────────────────────────────────────────────────
// useNotificationSettings — loads the workspace alert-preference toggles and
// saves them back.
//   GET /settings/notifications → the toggles
//   PUT /settings/notifications → save (Admin only)
// `save` rethrows so the page's Save-Changes handler can distinguish success
// from failure; the `error` it exposes drives the in-pane banner. Mirrors
// useCompanySettings.
// ─────────────────────────────────────────────────────────────────────────────
import { useState, useEffect, useCallback } from "react";
import {
  getNotificationSettings,
  updateNotificationSettings,
} from "../../../services/settingsService";

// Fallback used only until the first fetch resolves.
const DEFAULTS = {
  criticalSystem: true, securityThreats: true,
  compliance: false,    systemUpdates: false,
};

export function useNotificationSettings() {
  const [prefs,   setPrefs]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    let cancelled = false;
    getNotificationSettings()
      .then((data) => { if (!cancelled) setPrefs({ ...DEFAULTS, ...(data?.alerts ?? {}) }); })
      .catch((err) => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // toggle("compliance") → flips that one key
  const toggle = useCallback(
    (key) => (value) => setPrefs((prev) => ({ ...prev, [key]: value })),
    [],
  );

  const save = useCallback(async () => {
    if (!prefs) return;
    setError(null);
    try {
      await updateNotificationSettings(prefs);
    } catch (err) {
      setError(err.message || "Failed to save notification preferences.");
      throw err;
    }
  }, [prefs]);

  return { prefs, loading, error, toggle, save };
}
